import { h } from 'preact';
import {
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'preact/hooks';
import classNames from 'classnames';
import {
  getKustomerCore,
  setKustomerLocalStorage,
} from 'widget_main/globals/helpers';

import {
  KBContext,
  NavigationContext,
  SettingsContext,
} from 'widget_main/store';
import { CategoryCallbackResponse } from 'core_main/sdk/get_categories/types';
import { KB_ARTICLE_PATH, KB_SEARCH_PATH } from 'widget_main/globals/constants';
import ArticlesEmpty from 'widget_main/globals/assets/articlesEmpty.svg';
import { ArticleCallbackResponse } from 'core_main/sdk/get_articles/types';

import { useTranslations } from 'i18n/hooks';
import { getMappedLanguage } from 'i18n/helpers';
import KBSearchListItem from './KBSearchListItem';

import { createCategoryTree } from './helpers';
import styles from './kbSearchList.scss';
import { ARTICLE_TYPE } from './constants';
import { KBSearchListItemType } from './types';

import messages from './messages';

const kustomerCore = getKustomerCore();

const KBSearchList = () => {
  const kb = useContext(KBContext);
  const navigation = useContext(NavigationContext);
  const chatSettings = useContext(SettingsContext);
  const [loading, updateLoading] = useState(false);
  const [langSupported, updateLangSupported] = useState(true);
  const translations = useTranslations(messages);
  const listContainer = useRef<null | HTMLDivElement>(null);

  const lang = getMappedLanguage();

  const {
    categories,
    rootCategory,
    currentCategoryId,
    articleSearchResults,
  } = kb;

  const { knowledgeBaseId } = chatSettings;

  useEffect(() => {
    setKustomerLocalStorage('lastOpenPage', KB_SEARCH_PATH);
  }, []);

  useEffect(() => {
    if (!Object.keys(categories).length) {
      updateLoading(true);
      kustomerCore.getCategories({ lang, knowledgeBaseId }, (res, err) => {
        const langNotFound =
          err?.title === 'Not Found' && err?.source === 'lang';

        if (langNotFound) {
          updateLangSupported(false);
          updateLoading(false);
          return;
        }

        if (res) {
          const tree = createCategoryTree(res.categories);
          kb.updateCategories(tree, res.rootCategory);
          kb.updateCurrentCategoryId(res.rootCategory.categoryId);
          updateLoading(false);
        }
      });
    }
  }, [categories, kb, lang, knowledgeBaseId]);

  useLayoutEffect(() => {
    const currentList = listContainer?.current;
    if (currentList && currentList?.scrollTop) {
      currentList.scrollTop = 0;
    }
  }, [currentCategoryId]);

  const renderEmptyState = () => {
    return (
      <div className={styles.empty}>
        <img
          alt={translations.articlesEmptyAltText}
          className={styles.articlesEmpty}
          src={ArticlesEmpty}
        />
        <p className={styles.emptyMessage}>
          {translations.kbSearchListEmptyState}
        </p>
      </div>
    );
  };

  if (!langSupported) {
    return <div className={classNames(styles.list)}>{renderEmptyState()}</div>;
  }

  const renderLoader = () => {
    return <div className={styles.loader} />;
  };

  if (!currentCategoryId || loading || kb.articleSearchFetching) {
    return (
      <div className={classNames(styles.list, styles.listLoading)}>
        {renderLoader()}
      </div>
    );
  }

  const category = categories?.[currentCategoryId];
  const sortedChildCategories = category?.sortedChildCategories;
  const articles = category?.articles;

  const handleListItemClick = (type: KBSearchListItemType, id: string) => {
    if (type === ARTICLE_TYPE) {
      kb.updateCurrentArticleId(id);
      navigation.updatePage(KB_ARTICLE_PATH);
      return;
    }

    const categoryArticles = categories[id].articles;

    if (categories && !categoryArticles) {
      kustomerCore.getArticles(
        { categoryId: id, lang, knowledgeBaseId },
        (res, err) => {
          const updatedArticles = err ? [] : res.articles;

          kb.updateArticles(updatedArticles);

          kb.updateCategories(
            {
              ...categories,
              [id]: { ...categories[id], articles: updatedArticles },
            },
            rootCategory,
          );

          kb.updateCurrentCategoryId(id);
        },
      );

      return;
    }

    kb.updateCurrentCategoryId(id);
  };

  const renderHeader = () => {
    if (currentCategoryId !== rootCategory?.categoryId) {
      if (category) {
        return (
          <div className={styles.headerContainer}>
            <p className={styles.headerTitle}>{category.title}</p>
            <p className={styles.headerDescription}>{category.description}</p>
          </div>
        );
      }
    }

    return null;
  };

  const tree: (
    | ArticleCallbackResponse
    | CategoryCallbackResponse
  )[] = articleSearchResults || [
    ...(articles || []),
    ...(sortedChildCategories || []),
  ];

  return (
    <div ref={listContainer} className={classNames(styles.list)}>
      {renderHeader()}
      {tree?.map((item, index) => {
        const isLast = !tree[index + 1];
        return (
          <KBSearchListItem
            listItem={item}
            onClick={handleListItemClick}
            isLast={isLast}
          />
        );
      })}
      {tree?.length === 0 && renderEmptyState()}
    </div>
  );
};

export default KBSearchList;
